import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, Check, Loader2, X, AlertTriangle, ArrowRight, RotateCcw } from "lucide-react";

const STEPS = [
  { key: "upload", label: "Uploaden" },
  { key: "save", label: "Opslaan" },
  { key: "ai", label: "AI controleert" },
];

/**
 * Render-prop camera flow: children(openCamera) renders the trigger.
 * Foto → (restaurant kiezen) → POST /invoices → POST /invoices/{id}/ai_check → resultaat.
 */
export default function QuickInvoiceCapture({ children, onSaved, defaultRestaurantId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [restaurants, setRestaurants] = useState([]);
  const [image, setImage] = useState("");
  const [restaurantId, setRestaurantId] = useState(defaultRestaurantId || user?.restaurant_id || "");
  const [step, setStep] = useState(null); // null | 0 | 1 | 2 | "done" | "error"
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/restaurants").then((r) => {
      const list = r.data || [];
      setRestaurants(list);
      if (!restaurantId && list.length === 1) setRestaurantId(list[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { setImage(""); setStep(null); setResult(null); setError(""); };
  const close = () => { setOpen(false); reset(); };

  const openCamera = () => inputRef.current?.click();

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Foto te groot (max 4 MB)"); return; }
    setStep(0);
    setOpen(true);
    const reader = new FileReader();
    reader.onload = (e) => { setImage(e.target.result); setStep(null); };
    reader.readAsDataURL(file);
  };

  const run = async () => {
    if (!image) return;
    setError("");
    try {
      setStep(1);
      const inv = await api.post("/invoices", { image_url: image, description: "Onbekend", restaurant_id: restaurantId || null });
      onSaved?.(inv.data);
      setStep(2);
      const r = await api.post(`/invoices/${inv.data.id}/ai_check`);
      setResult(r.data);
      setStep("done");
      onSaved?.(inv.data);
      const d = r.data;
      const msg = d.matched
        ? (d.deviations > 0 ? `Factuur opgeslagen, ${d.deviations} afwijking${d.deviations === 1 ? "" : "en"} gevonden` : "Factuur opgeslagen, geen afwijkingen")
        : "Factuur opgeslagen — geen bijpassende bestelling gevonden";
      toast[d.matched && d.deviations > 0 ? "warning" : "success"](msg, {
        action: { label: "Bekijk", onClick: () => navigate(d.matched ? "/facturen?tab=controle" : "/facturen") },
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Er ging iets mis";
      setError(detail);
      setStep("error");
      onSaved?.();
    }
  };

  // Auto-start when only one restaurant applies
  useEffect(() => {
    if (open && image && step === null && restaurantId && restaurants.length <= 1) run();
  }, [open, image, step, restaurantId, restaurants.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const stepIndex = typeof step === "number" ? step : step === "done" ? 3 : step === "error" ? -1 : -2;

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
        onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="quick-invoice-input" />
      {children(openCamera)}

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={close} data-testid="quick-invoice-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-[#1B2D5C] text-white flex items-center justify-center"><Camera size={16} /></div>
                <div>
                  <div className="font-bold text-[#1B2D5C] leading-tight">Factuur fotograferen</div>
                  <div className="text-xs text-zinc-500">Opslaan en direct door de AI-controle</div>
                </div>
              </div>
              <button onClick={close} className="w-9 h-9 rounded-full hover:bg-zinc-100 inline-flex items-center justify-center text-zinc-500" data-testid="quick-invoice-close"><X size={18} /></button>
            </div>

            {image && (
              <img src={image} alt="" className="w-full max-h-52 object-contain rounded-xl border border-zinc-200 bg-zinc-50 mb-4" data-testid="quick-invoice-preview" />
            )}

            {step === null && image && restaurants.length > 1 && (
              <div className="space-y-3">
                <label className="overline block">Voor welk restaurant?</label>
                <div className="flex flex-wrap gap-2" data-testid="quick-invoice-restaurants">
                  {restaurants.map((r) => (
                    <button key={r.id} type="button" onClick={() => setRestaurantId(r.id)}
                      className={`h-9 px-4 rounded-full text-sm font-semibold border transition-colors ${restaurantId === r.id ? "bg-[#1B2D5C] text-white border-[#1B2D5C]" : "bg-white text-zinc-700 border-zinc-300 hover:border-[#1B2D5C]"}`}
                      data-testid={`quick-invoice-resto-${r.id}`}>
                      {r.name}
                    </button>
                  ))}
                </div>
                <button type="button" disabled={!restaurantId} onClick={run}
                  className="w-full h-12 rounded-full bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-50 text-white font-bold inline-flex items-center justify-center gap-2"
                  data-testid="quick-invoice-start">
                  Opslaan & controleren <ArrowRight size={16} />
                </button>
              </div>
            )}

            {(typeof step === "number" || step === "done" || step === "error") && (
              <ol className="grid grid-cols-3 gap-2 mb-4" data-testid="quick-invoice-steps">
                {STEPS.map((s, i) => {
                  const done = stepIndex > i;
                  const active = stepIndex === i;
                  const failed = step === "error" && !done;
                  return (
                    <li key={s.key} className={`rounded-xl p-2.5 text-center text-xs font-semibold border transition-colors ${done ? "bg-emerald-50 border-emerald-200 text-emerald-700" : active ? "bg-[#1B2D5C] border-[#1B2D5C] text-white" : failed ? "bg-red-50 border-red-200 text-red-600" : "bg-zinc-50 border-zinc-200 text-zinc-400"}`}
                      data-testid={`quick-invoice-step-${s.key}`}>
                      <div className="flex justify-center mb-1">
                        {done ? <Check size={14} /> : active ? <Loader2 size={14} className="animate-spin" /> : failed ? <AlertTriangle size={14} /> : <span className="w-3.5 h-3.5 rounded-full border border-current inline-block" />}
                      </div>
                      {s.label}
                    </li>
                  );
                })}
              </ol>
            )}

            {step === "done" && result && (
              <div className="space-y-3" data-testid="quick-invoice-result">
                <div className={`rounded-xl p-3 text-sm ${result.matched ? (result.deviations > 0 ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800") : "bg-zinc-50 text-zinc-700"}`}>
                  {result.matched ? (
                    <>
                      <div className="font-bold">Gekoppeld aan {result.order_readable_id} · {result.deviations} afwijking{result.deviations === 1 ? "" : "en"}</div>
                      {result.supplier && <div className="text-xs mt-0.5">Leverancier: {result.supplier}{result.amount ? ` · € ${Number(result.amount).toFixed(2)}` : ""}</div>}
                      {result.advies && <div className="text-xs mt-1 opacity-80">{result.advies}</div>}
                    </>
                  ) : (
                    <>
                      <div className="font-bold">Opgeslagen{result.supplier ? ` — ${result.supplier}` : ""}</div>
                      <div className="text-xs mt-0.5">Geen bijpassende bestelling gevonden. Je kunt de factuur later handmatig koppelen.</div>
                    </>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => { reset(); openCamera(); }} className="h-11 rounded-full border border-zinc-300 hover:border-[#1B2D5C] text-sm font-semibold inline-flex items-center justify-center gap-1.5" data-testid="quick-invoice-another">
                    <Camera size={15} /> Nog een factuur
                  </button>
                  <button onClick={() => { close(); navigate(result.matched ? "/facturen?tab=controle" : "/facturen"); }} className="h-11 rounded-full bg-[#1B2D5C] text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5" data-testid="quick-invoice-view">
                    Bekijk factuur <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            )}

            {step === "error" && (
              <div className="space-y-3" data-testid="quick-invoice-error">
                <div className="rounded-xl p-3 text-sm bg-red-50 text-red-700 flex items-start gap-2"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> {error}</div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => { reset(); openCamera(); }} className="h-11 rounded-full border border-zinc-300 text-sm font-semibold inline-flex items-center justify-center gap-1.5"><RotateCcw size={15} /> Opnieuw</button>
                  <button onClick={() => { close(); navigate("/facturen"); }} className="h-11 rounded-full bg-[#1B2D5C] text-white text-sm font-semibold">Naar facturen</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export function QuickInvoiceFab({ onSaved }) {
  return (
    <QuickInvoiceCapture onSaved={onSaved}>
      {(openCamera) => (
        <button type="button" onClick={openCamera}
          className="lg:hidden fixed right-4 bottom-20 z-40 w-14 h-14 rounded-full bg-[#1B2D5C] text-white shadow-xl shadow-[#1B2D5C]/30 flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Factuur fotograferen" data-testid="quick-invoice-fab">
          <Camera size={22} />
        </button>
      )}
    </QuickInvoiceCapture>
  );
}
